import { Dispatch, type FC } from 'react';

// MUI
import { Box, Typography, Tooltip } from '@mui/material';

// screen
import { TemplateLayout, TemplateSettings } from 'modules/map/components/daily-dish/types';
import { FIELD_WIDTHS, LayoutId } from 'modules/map/components/daily-dish/constants';
import { SettingsAction } from 'modules/map/components/daily-dish/reducer';
import StyledLabel from 'modules/map/components/daily-dish/components/utils/StyledLabel';
import { LAYOUT_CONFIG } from 'modules/map/components/daily-dish/config';

interface TemplateLayoutSelectorProps {
  settings: TemplateSettings;
  dispatch: Dispatch<SettingsAction>;
}

const TemplateLayoutSelector: FC<TemplateLayoutSelectorProps> = (props) => {
  const { settings, dispatch } = props;

  const items: TemplateLayout[] = LAYOUT_CONFIG.map((layout) => ({
    id: layout.id,
    element: <layout.element />,
    desciption: layout.description,
    type: layout.type,
  }));

  const handleSelectLayout = (layoutId: number) => {
    dispatch({ type: 'UPDATE_LAYOUT', layoutId });
  };

  const handleLayoutAlign = (align: 'left' | 'right') => {
    dispatch({ type: 'UPDATE_LAYOUT_ALIGN', align });
  };

  const showAlignOptions = settings.general.layoutId === LayoutId.TWO_COLUMNS_LEFT;

  return (
    <>
      <StyledLabel label="Bố cục" sx={{ fontWeight: 600 }} />
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-around',
        }}
      >
        {items.map((item, index) => {
          const isSelected = item.id === settings.general.layoutId;

          return (
            <Tooltip key={index} title={item.desciption} arrow>
              <Box
                sx={{
                  flexBasis: FIELD_WIDTHS.QUARTER,
                  position: 'relative',
                  border: `1px solid ${isSelected ? '#FFB22C' : '#D6D6D6'} `,
                  aspectRatio: '1 / 1',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  boxShadow: isSelected ? '0 0 10px #FFB22C' : 'none',
                }}
                onClick={() => handleSelectLayout(item.id)}
              >
                <Box>{item.element}</Box>
                <Typography
                  sx={{
                    position: 'absolute',
                    backgroundColor: 'rgba(212, 255, 205, 0.2)',
                    color: '#46B52B',
                    padding: '2px 6px',
                    top: 0,
                    right: 0,
                    fontSize: '12px',
                  }}
                >
                  {item.type}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      {showAlignOptions && (
        <Box sx={{ mt: 2 }}>
          <StyledLabel label="Căn chỉnh bố cục" sx={{ fontWeight: 600 }} />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box
              onClick={() => handleLayoutAlign('left')}
              sx={{
                flex: 1,
                padding: '12px',
                border: `2px solid ${
                  settings.general.layoutAlign === 'left' ? '#FFB22C' : '#D6D6D6'
                }`,
                borderRadius: '8px',
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: settings.general.layoutAlign === 'left' ? '#FFF9E6' : '#fff',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: '#FFB22C',
                  backgroundColor: '#FFF9E6',
                },
              }}
            >
              <Typography>Hai cột lệch trái</Typography>
            </Box>
            <Box
              onClick={() => handleLayoutAlign('right')}
              sx={{
                flex: 1,
                padding: '12px',
                border: `2px solid ${
                  settings.general.layoutAlign === 'right' ? '#FFB22C' : '#D6D6D6'
                }`,
                borderRadius: '8px',
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: settings.general.layoutAlign === 'right' ? '#FFF9E6' : '#fff',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: '#FFB22C',
                  backgroundColor: '#FFF9E6',
                },
              }}
            >
              <Typography>Hai cột lệch phải</Typography>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default TemplateLayoutSelector;
